import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { DiscriminatedTaskCreatedEvent } from '@/services/api/generated/matterChangesService';
import { formatTaskDate } from '@/utils/taskDateUtils';

import {
  ActivityActions,
  ActivityBody,
  ActivityDescription,
  ActivityIcon,
  ActivityItem,
  ActivityLink,
  ActivityTitle,
  CollapsibleList,
} from '../activity-feed-v2';
import { getUserFromEvent } from '../activity-feed-v2/ActivityDiffList';
import { formatEnumValue } from '../activity-feed-v2/utils';

export interface TaskCreatedEventProps {
  event: DiscriminatedTaskCreatedEvent;
}

interface TaskDetailField {
  label: string;
  value: string | number | null | undefined;
}

export const TaskCreatedEvent: React.FC<TaskCreatedEventProps> = ({
  event,
}) => {
  const { task } = event.payload;
  const user = getUserFromEvent(event);

  const icon = (
    <ActivityIcon
      icon={
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100">
          <ClipboardDocumentListIcon className="h-4 w-4 text-blue-600" />
        </div>
      }
    />
  );

  const taskDetailFields: TaskDetailField[] = [
    { label: 'Description', value: task?.description },
    { label: 'Group', value: task?.group ? formatEnumValue(task.group) : null },
  ];

  const detailItems = taskDetailFields
    .filter(
      ({ value }) => value !== null && value !== undefined && value !== '',
    )
    .map(({ label, value }) => `${label}: ${value}`);

  const body = (
    <ActivityBody>
      {task?.name && <ActivityTitle>{task.name}</ActivityTitle>}
      <div className="mt-1 space-y-1">
        {task?.type && (
          <ActivityDescription>
            Type: {formatEnumValue(task.type)}
          </ActivityDescription>
        )}
        {task?.due_date && (
          <ActivityDescription>
            Due: {formatTaskDate(task.due_date)}
          </ActivityDescription>
        )}
      </div>
      {detailItems.length > 0 && (
        <CollapsibleList
          items={detailItems}
          showLabel="Show Details"
          hideLabel="Hide Details"
        />
      )}
    </ActivityBody>
  );

  const actions = task?.id && (
    <ActivityActions>
      <ActivityLink
        href={`/matters/${event.matter_id}/updates?task_id=${task.id}`}
      >
        View Task
      </ActivityLink>
    </ActivityActions>
  );

  return (
    <ActivityItem
      icon={icon}
      title="Task Created"
      timestamp={new Date(event.occurred_at ?? new Date())}
      user={user}
      body={body}
      actions={actions}
    />
  );
};
